import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../types/rootstate'
import { strings } from '../lib/localization'
import escapeHandler from '../lib/escape-handler'
import imboxLogo from '../fonts/icon-imboxlogo.svg'
import { BrandingState } from '../reducers/branding-reducer'
import { brandingHide, brandingShow } from '../actions/branding-actions'

import '../sass/brandingform.scss'
import '../sass/answers/chatbutton.scss'

export const BrandingIcon = ({ type }: { type: BrandingState['type'] }) => {
  const dispatch = useDispatch()

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      dispatch(brandingShow(type))
    }
  }

  const onClick = () => {
    dispatch(brandingShow(type))
  }

  return (
    <div className='branding'>
      <div
        onClick={onClick}
        role='button'
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {strings['brandingText']} <img src={imboxLogo} alt='ImBox' />
      </div>
    </div>
  )
}

export const Branding: React.FC = () => {
  const closeButtonRef = React.useRef<HTMLDivElement>()
  const dispatch = useDispatch()
  const userId = useSelector((state: RootState) => state.widget.userId)
  const brandingType = useSelector((state: RootState) => state.branding.type)
  const show = useSelector((state: RootState) => state.branding.show)
  const currentUrl = useSelector((state: RootState) =>
    encodeURIComponent(state.widget.host.url)
  )

  React.useEffect(() => {
    if (!show) {
      return
    }

    const handleEscape = () => {
      dispatch(brandingHide())
    }

    escapeHandler.registerEscapeListener(handleEscape)

    return () => {
      escapeHandler.removeEscapeListener(handleEscape)
    }
  }, [show])

  React.useEffect(() => {
    if (show) {
      closeButtonRef.current?.focus()
    }
  }, [show])

  const handleBackKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      dispatch(brandingHide())
    }
  }

  const handleOnClose = () => {
    dispatch(brandingHide())
  }

  if (brandingType === 'none' || !show) {
    return null
  }

  const brandingContent = {
    __html:
      brandingType === 'online'
        ? strings['brandingPageOnline']
        : brandingType === 'answers'
        ? strings['brandingPageAnswers']
        : strings['brandingPageForms']
  }
  const backButtonText =
    brandingType === 'online'
      ? strings['brandingPageOnlineBackButton']
      : brandingType === 'answers'
      ? strings['brandingPageAnswersBackButton']
      : strings['brandingPageFormsBackButton']

  return (
    <>
      <div
        className='brandingform'
        role='dialog'
        aria-describedby='imboxBranding'
      >
        <div
          className='brandingcontent'
          dangerouslySetInnerHTML={brandingContent}
          id='imboxBranding'
        />
        <a
          target='_blank'
          rel='noopener noreferrer'
          href={
            strings['brandingLink'] +
            '?utm_source=' +
            currentUrl +
            '&utm_campaign=' +
            userId +
            '&utm_medium=widget-v2-' +
            brandingType
          }
        >
          {strings['brandingPageVisitButton']}
        </a>

        <div
          ref={closeButtonRef}
          className='input-line chatbutton accentSecondaryButton'
          role='button'
          tabIndex={0}
          onKeyDown={handleBackKeyDown}
          onClick={handleOnClose}
        >
          <label>{backButtonText}</label>
        </div>
      </div>
      <div
        className='overlay'
        role='button'
        tabIndex={-1}
        onKeyDown={handleBackKeyDown}
        onClick={handleOnClose}
      />
    </>
  )
}
